import { FigureCaptionPlugin } from '@alpod-ai-writer/plate-plugins/figure-caption/react';
import { SourceCaptionPlugin } from '@alpod-ai-writer/plate-plugins/source-caption/react';
import type { DropdownMenuProps } from '@radix-ui/react-dropdown-menu';
import {
    ParagraphPlugin,
    useEditorRef,
    useSelectionFragmentProp,
} from '@udecode/plate/react';
import { HEADING_KEYS } from '@udecode/plate-heading';
import {
    BookMarkedIcon,
    Heading1Icon,
    Heading2Icon,
    Heading3Icon,
    Heading4Icon,
    Heading5Icon,
    PaperclipIcon,
    PencilLineIcon,
    PilcrowIcon,
} from 'lucide-react';
import { useMemo } from 'react';

import { ATTACHMENT_HEADING_KEY } from '../../plugins/attachmentHeadingPlugin';
import { getBlockType, setBlockType, STRUCTURAL_TYPES } from '../../utils/transforms.utils';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuTrigger,
    useOpenState,
} from '../common/dropdownMenu.component';
import { ToolbarButton } from '../common/toolbar.component';

const turnIntoItems = [
    {
        icon: <PilcrowIcon />,
        keywords: ['paragraph'],
        label: 'Odstavec',
        value: ParagraphPlugin.key,
    },
    {
        icon: <Heading1Icon />,
        keywords: ['title', 'h1'],
        label: 'Nadpis 1',
        value: HEADING_KEYS.h1,
    },
    {
        icon: <Heading2Icon />,
        keywords: ['subtitle', 'h2'],
        label: 'Nadpis 2',
        value: HEADING_KEYS.h2,
    },
    {
        icon: <Heading3Icon />,
        keywords: ['subtitle', 'h3'],
        label: 'Nadpis 3',
        value: HEADING_KEYS.h3,
    },
    {
        icon: <Heading4Icon />,
        keywords: ['subtitle', 'h4'],
        label: 'Nadpis 4',
        value: HEADING_KEYS.h4,
    },
    {
        icon: <Heading5Icon />,
        keywords: ['subtitle', 'h5'],
        label: 'Nadpis 5',
        value: HEADING_KEYS.h5,
    },
    {
        icon: <PaperclipIcon />,
        label: 'Nadpis Přílohy',
        value: ATTACHMENT_HEADING_KEY,
    },
    {
        icon: <PencilLineIcon />,
        label: 'Popisek',
        value: FigureCaptionPlugin.key
    },
    {
        icon: <BookMarkedIcon />,
        label: 'Zdroj',
        value: SourceCaptionPlugin.key
    }
];

export function TurnIntoDropdownMenu(props: DropdownMenuProps) {
    const editor = useEditorRef();
    const openState = useOpenState();
    const value = useSelectionFragmentProp({
        defaultValue: ParagraphPlugin.key,
        structuralTypes: STRUCTURAL_TYPES,
        getProp: (node) => getBlockType(node as any),
    });
    const selectedItem = useMemo(
        () =>
            turnIntoItems.find(
                (item) => item.value === (value ?? ParagraphPlugin.key)
            ) ?? turnIntoItems[0],
        [value]
    );
    return (
        <DropdownMenu modal={false} {...openState} {...props}>
            <DropdownMenuTrigger asChild>
                <ToolbarButton
                    className="min-w-[125px]"
                    pressed={openState.open}
                    tooltip="Styl odstavce"
                    isDropdown
                >
                    {selectedItem.label}
                </ToolbarButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                className="ignore-click-outside/toolbar min-w-0"
                onCloseAutoFocus={(e) => {
                    e.preventDefault();
                    editor.tf.focus();
                }}
                align="start"
            >
                <DropdownMenuRadioGroup
                    value={value}
                    onValueChange={(type) => {
                        setBlockType(editor, type);
                    }}
                    label="Styl odstavce"
                >
                    {turnIntoItems.map(({ icon, label, value: itemValue }) => (
                        <DropdownMenuRadioItem
                            key={itemValue}
                            className="min-w-[180px]"
                            value={itemValue}
                        >
                            {icon}
                            {label}
                        </DropdownMenuRadioItem>
                    ))}
                </DropdownMenuRadioGroup>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
